import {
  Button,
  Container,
  Group,
  Modal,
  Select,
  Stack,
  Text,
} from "@mantine/core";
import { IconUpload } from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";
import { useState } from "react";
import FileDropzone from "@/components/UploadDocModal/FileDropzone/FileDropzone";
import type { FileDetails } from "@/types";
import { forms, MAX_FILE_SIZE_MB } from "@/constants";
import { showNotification } from "@mantine/notifications";
import { useUploadDoc } from "../../hooks/useUploadDoc";
import { useParams } from "react-router-dom";
import { useProfileStore } from "../../store/profileStore";
import { convertToBase64 } from "@/components/Tax Pro Only/Modals/EmailModal/utils";
import { generateTaxYear } from "@/utilities";
import type { DocumentUserType } from "@pages/ClientProfile/types.ts";

const DocumentUpload = (): JSX.Element => {
  const [opened, { open, close: closeModal }] = useDisclosure(false);
  const [selectedForm, setSelectedForm] = useState<string | null>("Other");
  const [userType, setUserType] = useState<DocumentUserType>("taxClient");
  const [fileData, setFileData] = useState({
    fileDetails: [] as FileDetails[],
    file: null as File | null,
  });

  const { pollOCRJob, uploadDocAndReturnJobInfo, isUploading } = useUploadDoc();

  const { activeYear } = useProfileStore();

  // Replace these with real values in your app
  const { userId } = useParams();
  const clientId = userId ?? "";
  const taxYear = activeYear ?? generateTaxYear();

  const resetModal = (): void => {
    setFileData({ fileDetails: [], file: null });
    setSelectedForm("Other");
    setUserType("taxClient");
  };

  const close = (): void => {
    resetModal();
    closeModal();
  };

  const setFile = async (files: File[]): Promise<void> => {
    try {
      const file = files[0];

      const base64 = await convertToBase64(file);

      const newFileDetails = [
        {
          name: file.name,
          size: file.size,
          type: file.type,
          extension: file.name.split(".").pop(),
          file,
          base64, // optionally store this
        },
      ];

      setFileData((prev) => ({
        ...prev,
        fileDetails: [...prev.fileDetails, ...newFileDetails],
        file,
      }));
    } catch (error) {
      console.error(error);
    }
  };

  const handleUpload = async (): Promise<void> => {
    if (!fileData.file) return;

    const formType = selectedForm || "Other";
    const file = fileData.file; // Store in local variable for type narrowing

    const data = fileData.fileDetails[0].base64;

    try {
      const { success, jobId, docId } = await uploadDocAndReturnJobInfo({
        clientId,
        taxYear,
        userType,
        form: formType,
        fileName: file.name,
        data: data as string,
      });

      if (success) {
        showNotification({
          title: "Upload successful",
          message: "Your document has been uploaded.",
          color: "green",
        });

        close(); // Close modal immediately

        if (jobId) {
          // Trigger background polling
          await pollOCRJob(jobId, docId ?? "");
        } else {
          showNotification({
            message: "Could not start job to process document.",
            color: "red",
          });
        }
      }
    } catch {
      showNotification({
        title: "Upload failed",
        message: "Something went wrong while uploading. Please try again.",
        color: "red",
      });
    }
  };

  const isUploadDisabled = !fileData.file || isUploading;

  return (
    <>
      <Container w={"100%"} pt={10} ta={"center"} pl={0} pr={0}>
        <Button
          w={"100%"}
          rightSection={<IconUpload color="gray" size={16} />}
          variant="outline"
          size="xl"
          bg="#f5f5f5"
          onClick={open}
          data-tooltip-id="upload-doc"
          styles={{
            root: {
              borderStyle: "dotted",
              borderWidth: "2px",
              borderSpacing: "4px",
              borderColor: "gray",
            },
          }}
        >
          <Text size="sm" c="dimmed">
            Upload new document
          </Text>
        </Button>
        <Text size="sm" c="dimmed" mt={5}>
          Accepted File Format: PDF, JPG, PNG, GIF, HEIF, HEIC (max of{" "}
          {MAX_FILE_SIZE_MB}MB)
        </Text>
      </Container>

      <Modal
        opened={opened}
        onClose={close}
        title={
          <Container mt={"lg"}>
            <Text size="lg" fw={500} mb={5}>
              Upload Documents
            </Text>
            <Text size="sm" c="dimmed">
              Upload a document to your client’s tax information
            </Text>
          </Container>
        }
        radius="md"
        size="lg"
      >
        <Stack>
          <Container w={"100%"} pl={20} pr={20} mt={10}>
            <Select
              data={forms}
              label="Form Type"
              value={selectedForm}
              onChange={setSelectedForm}
              mb={10}
              placeholder="Select a form"
              searchable
            />
            <Select
              label="Document Owner"
              value={userType}
              onChange={(value) => setUserType(value as DocumentUserType)}
              data={[
                { value: "taxClient", label: "Taxpayer" },
                { value: "spouse", label: "Spouse" },
                { value: "dependent", label: "Dependent" },
              ]}
              required
            />
          </Container>

          <Container w={"100%"} ta={"center"} p={20} pt={0}>
            <FileDropzone
              setFile={setFile}
              fileDetails={fileData.fileDetails}
            />
          </Container>

          <Group justify="flex-end">
            <Button variant="default" onClick={close}>
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              disabled={isUploadDisabled}
              loading={isUploading}
            >
              Upload
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};

export default DocumentUpload;
